import { useRef, useState } from "react";
import { ArrowUpRight } from "lucide-react";
import { COLORS } from "../styles/tokens";
import type { SignalPackage } from "../types/realsynq";
import { BREAKPOINTS } from "../styles/tokens";
import { usePrefersReducedMotion } from "../hooks";

interface PackageCardProps {
  pkg: SignalPackage;
  onOpen: () => void;
  index: number;
}

export function PackageCard({ pkg, onOpen, index }: PackageCardProps) {
  const Icon = pkg.icon;
  const cardRef = useRef<HTMLButtonElement>(null);
  const [tilt, setTilt] = useState({ x: 0, y: 0 });
  const [hover, setHover] = useState(false);
  const prefersReducedMotion = usePrefersReducedMotion();

  const onMove = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (prefersReducedMotion) return;
    if (window.innerWidth < BREAKPOINTS.mobile) return;
    const r = cardRef.current?.getBoundingClientRect();
    if (!r) return;
    const x = ((e.clientX - r.left) / r.width - 0.5) * 4;
    const y = ((e.clientY - r.top) / r.height - 0.5) * -4;
    setTilt({ x, y });
  };
  const onLeave = () => { setTilt({ x: 0, y: 0 }); setHover(false); };

  // Multi-layer elevation: inset top highlight + ambient + accent-tinted cast.
  const restShadow =
    "inset 0 1px 0 rgba(255,255,255,0.05), 0 2px 6px -3px rgba(0,0,0,0.5), 0 12px 32px -22px rgba(0,0,0,0.85)";
  const hoverShadow =
    `inset 0 1px 0 rgba(255,255,255,0.08), 0 4px 12px -4px rgba(0,0,0,0.6), 0 28px 64px -26px ${pkg.accent}66`;

  return (
    <button
      ref={cardRef}
      onClick={onOpen}
      onMouseMove={onMove}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={onLeave}
      onFocus={() => setHover(true)}
      onBlur={() => setHover(false)}
      className="pkg-card shimmer-border"
      style={{
        textAlign: "left",
        background: COLORS.bgCard,
        backdropFilter: "blur(12px)",
        WebkitBackdropFilter: "blur(12px)",
        border: `1px solid ${hover ? pkg.accent : COLORS.border}`,
        borderRadius: 12,
        padding: 24,
        position: "relative",
        overflow: "hidden",
        cursor: "pointer",
        color: COLORS.ink,
        font: "inherit",
        willChange: "transform",
        transform: `perspective(800px) rotateY(${tilt.x}deg) rotateX(${tilt.y}deg) translateY(${hover ? -6 : 0}px) scale(${hover ? 1.015 : 1})`,
        boxShadow: hover ? hoverShadow : restShadow,
        animation: `fadeUp 0.5s ${0.05 * index}s ease-out both`,
      }}
    >
      {/* Glass top-edge highlight (depth) */}
      <div
        aria-hidden
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: 1,
          background: `linear-gradient(90deg, transparent, ${pkg.accent}55, transparent)`,
          opacity: hover ? 1 : 0.5,
          pointerEvents: "none",
        }}
      />
      <div
        style={{
          position: "absolute",
          top: 0,
          right: 0,
          width: 100,
          height: 100,
          background: `radial-gradient(circle at top right, ${pkg.accent}${hover ? "33" : "1f"}, transparent 70%)`,
          transition: "background 0.35s",
          pointerEvents: "none",
        }}
      />

      <div
        aria-hidden
        style={{
          position: "absolute",
          top: 0,
          right: 0,
          width: 32,
          height: 32,
          pointerEvents: "none",
        }}
      >
        <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
          <path d="M32 0 L32 12 L20 12" stroke={pkg.accent} strokeWidth="1" strokeOpacity="0.3" />
          <circle cx="32" cy="0" r="2" fill={pkg.accent} fillOpacity="0.5" />
        </svg>
      </div>

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 24 }}>
        <div
          style={{
            width: 44,
            height: 44,
            borderRadius: 10,
            background: `${pkg.accent}15`,
            border: `1px solid ${pkg.accent}40`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            position: "relative",
          }}
        >
          <Icon size={20} color={pkg.accent} strokeWidth={2} />
          <div
            aria-hidden
            style={{
              position: "absolute",
              inset: -4,
              borderRadius: 14,
              background: `radial-gradient(circle, ${pkg.accent}1a, transparent 70%)`,
              pointerEvents: "none",
            }}
          />
        </div>
        <ArrowUpRight size={16} color={COLORS.inkMute} />
      </div>

      <div className="display" style={{ fontSize: 24, fontWeight: 700, marginBottom: 4 }}>
        {pkg.name}
      </div>
      <div style={{ fontSize: 13, color: COLORS.inkDim, marginBottom: 20 }}>
        {pkg.tagline}
      </div>

      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 20 }}>
        {pkg.verticals.slice(0, 3).map(v => (
          <span
            key={v}
            className="mono"
            style={{
              fontSize: 10,
              padding: "3px 7px",
              background: "rgba(10, 19, 32, 0.6)",
              border: `1px solid ${COLORS.border}`,
              borderRadius: 3,
              color: COLORS.inkDim,
              letterSpacing: "0.05em",
            }}
          >
            {v.toUpperCase()}
          </span>
        ))}
        {pkg.verticals.length > 3 && (
          <span className="mono" style={{ fontSize: 10, color: COLORS.inkMute, padding: "3px 0" }}>
            +{pkg.verticals.length - 3}
          </span>
        )}
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 16,
          paddingTop: 16,
          borderTop: `1px solid ${COLORS.border}`,
        }}
      >
        <div>
          <div className="mono" style={{ fontSize: 9, color: COLORS.inkMute, letterSpacing: "0.1em", marginBottom: 4 }}>
            SIGNALS
          </div>
          <div className="mono" style={{ fontSize: 16, color: COLORS.ink, fontWeight: 600 }}>
            {pkg.signalCount}
          </div>
        </div>
        <div>
          <div className="mono" style={{ fontSize: 9, color: COLORS.inkMute, letterSpacing: "0.1em", marginBottom: 4 }}>
            TTL RANGE
          </div>
          <div className="mono" style={{ fontSize: 13, color: COLORS.ink, fontWeight: 500 }}>
            {pkg.ttlRange}
          </div>
        </div>
      </div>
    </button>
  );
}
